import type { ChangeEvent, ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef, useId } from 'react';
import { styled, css } from 'styled-components';
import { isNonEmptyString, isNotUndefined } from '@wistia/type-guards';
import { visuallyHiddenStyle } from '../../css/visuallyHiddenStyle';
import { getColorScheme } from '../../private/helpers/getColorScheme';
import { useRadioGroup } from '../FormGroup/RadioGroup';
import type { Spacings } from '../../css/designTokens/spacing';

const checkedStyles = css`
  --wui-radio-card-border-color: var(--wui-color-focus-ring);
  --wui-color-icon: var(--wui-color-icon-selected);
  --wui-radio-card-background-color: var(--wui-color-bg-surface-info);
  --wui-color-text: var(--wui-color-text-info);
  --wui-color-text-secondary: var(--wui-color-text-info);
`;

const disabledStyles = css`
  --wui-radio-card-border-color: var(--wui-color-border-disabled);
  --wui-radio-card-background-color: var(--wui-color-bg-surface-disabled);
  --wui-radio-card-cursor: not-allowed;
  --wui-color-icon: var(--wui-color-icon-disabled);
  --wui-color-text: var(--wui-color-text-disabled);
  --wui-color-text-secondary: var(--wui-color-text-disabled);
`;

const focusStyles = css`
  outline: 2px solid var(--wui-color-focus-ring);
`;

const imageCoverStyles = css`
  --wui-radio-card-image-inset: 0px;
  --wui-radio-card-border-width: 0px;
  --wui-inset-shadow-width: 1px;
  --wui-radio-card-border-color: rgb(0 0 0 / 25%);

  overflow: hidden;

  &:has(input:checked) {
    --wui-radio-card-border-color: var(--wui-color-focus-ring);
    --wui-radio-card-image-inset: 2px;
    --wui-inset-shadow-width: 2px;
  }

  &:hover:not(:has(input:disabled, input:checked)) {
    --wui-radio-card-border-color: rgb(0 0 0 / 50%);
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    border-radius: var(--wui-radio-card-border-radius);
    box-shadow:
      inset 0 0 0 var(--wui-inset-shadow-width) var(--wui-radio-card-border-color),
      inset 0 0 0 calc(var(--wui-radio-card-image-inset) * 2) var(--wui-radio-card-background-color);
    transition: all var(--wui-motion-duration-02) var(--wui-motion-ease);
  }
`;

type StyledCardProps = {
  $aspectRatio: string;
  $isGated?: boolean;
  $padding: Spacings;
};

export const StyledCard = styled.label<StyledCardProps>`
  --wui-radio-card-border-color: var(--wui-color-border-secondary);
  --wui-radio-card-background-color: var(--wui-color-bg-surface);
  --wui-radio-card-cursor: pointer;
  --wui-radio-card-border-width: 1px;
  --wui-radio-card-border-radius: var(--wui-border-radius-02);

  position: relative;
  display: inline-flex;
  padding: ${({ $padding }) => `var(--wui-${$padding})`};
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  border: var(--wui-radio-card-border-width) solid var(--wui-radio-card-border-color);
  border-radius: var(--wui-radio-card-border-radius);
  background-color: var(--wui-radio-card-background-color);
  cursor: var(--wui-radio-card-cursor);
  overflow: hidden;
  transition: all var(--wui-motion-duration-03) var(--wui-motion-ease);

  svg {
    transition: all var(--wui-motion-duration-03) var(--wui-motion-ease);
  }

  &:hover {
    --wui-radio-card-border-color: var(--wui-color-border-secondary-hover);
  }

  &:active {
    --wui-radio-card-border-color: var(--wui-color-border-secondary-active);
  }

  &:has(input:checked) {
    ${({ $isGated }) => ($isGated ? getColorScheme('purple') : checkedStyles)}
  }

  &:has(input:disabled) {
    ${disabledStyles}
  }

  &:has(input:focus-visible) {
    ${focusStyles}
  }

  &:has([data-wui-radio-card-image]) {
    &:has(input:disabled) {
      [data-wui-radio-card-image] {
        filter: grayscale(100%);
      }
    }

    &:has([data-wui-radio-card-image='cover']) {
      ${imageCoverStyles}
    }
  }
`;

const StyledHiddenInput = styled.input`
  ${visuallyHiddenStyle}
`;

export type RadioCardRootProps = Omit<ComponentPropsWithoutRef<'input'>, 'onChange' | 'value'> & {
  /**
   * Indicates the state of the radio
   */
  checked?: boolean;
  /**
   * The content of the card
   */
  children: ReactNode;
  /**
   * Disables the card
   */
  disabled?: boolean;
  /**
   * Provides an ID for the input
   */
  id?: string;
  /**
   * Whether to show the gated styles
   */
  isGated?: boolean | undefined;
  /**
   * The name attribute of the input
   */
  name?: string;
  /**
   * Callback function invoked when the value changes
   */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /**
   * The value attribute of the input
   */
  value: string;
  /**
   * The aspect ratio of the card
   */
  aspectRatio?: string | undefined;
  /**
   * The padding of the card
   */
  padding?: Spacings;
};

export const RadioCardRoot = forwardRef<HTMLInputElement, RadioCardRootProps>(
  (
    {
      children,
      disabled = false,
      id,
      isGated = false,
      name,
      onChange,
      value,
      aspectRatio = 'initial',
      padding = 'space-04',
      checked,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const computedId = isNonEmptyString(id) ? id : `wistia-ui-radio-${generatedId}`;
    const radioGroupContext = useRadioGroup();

    // use context values if provided, otherwise fall back to the props
    const contextName = radioGroupContext?.name;
    const contextOnChange = radioGroupContext?.onChange;
    const contextValue = radioGroupContext?.value;
    const radioName = name ?? contextName;
    const handleOnChange = onChange ?? contextOnChange;
    const isChecked =
      isNotUndefined(value) && isNotUndefined(contextValue) ? contextValue === value : checked;

    return (
      <StyledCard
        $aspectRatio={aspectRatio}
        $isGated={isGated}
        $padding={padding}
        htmlFor={computedId}
      >
        <StyledHiddenInput
          {...props}
          ref={ref}
          checked={isChecked}
          disabled={disabled}
          id={computedId}
          name={radioName}
          onChange={handleOnChange}
          type="radio"
          value={value}
        />
        {children}
      </StyledCard>
    );
  },
);

RadioCardRoot.displayName = 'RadioCardRoot';
